import React, { useCallback } from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  BreakdownTableIndex,
  BreakdownCell,
  BreakdownCellContext,
} from './types';
import type { DecoratedDimension } from '@/utils/breakdown/decorateDimensions';
import type { VisibleDimension } from '@/utils/breakdown/buildVisibleDimensions';
import {
  aggregateForScope,
  type AggregateForScopeParams,
} from '@/utils/breakdown/aggregationHelpers';
import { getCell, hasSubDimensions } from './indexing';
import { DimensionHeader } from './DimensionHeader';
import { Z_INDEX } from './constants';

/** Avoid string literals in `hasSubDimensions` calls (i18n eslint treats them as JSX-adjacent). */
const DIM_COL = 'column' as const;
const DIM_ROW = 'row' as const;

interface BreakdownTableDataRowProps {
  rowData: DecoratedDimension;
  rowIndex: number;
  isLastRow: boolean;
  index: BreakdownTableIndex;
  expandedRows: Set<string>;
  expandedColumns: Set<string>;
  visibleColumns: VisibleDimension[];
  decoratedColumns: DecoratedDimension[];
  fillMissingSubRows: boolean;
  fillMissingSubColumns: boolean;
  subRowHasData: (row: string, subRow: string) => boolean;
  subColumnHasData: (column: string, subColumn: string) => boolean;
  xDim2Order?: string[];
  yDim2Order?: string[];
  aggregateCellValues?: (
    values: BreakdownCell['value'][],
  ) => BreakdownCell['value'];
  renderCell: (context: BreakdownCellContext) => React.ReactNode;
  renderLabel: (params: {
    value: string;
    isColumn?: boolean;
    isSubColumn?: boolean;
    isRow?: boolean;
    isSubRow?: boolean;
  }) => React.ReactNode;
  hideVerticalLines: boolean;
  cellNoVerticalBordersClasses: string;
  borderClasses: (
    r: { isFirstInGroup: boolean; isLastInGroup: boolean },
    c: { isFirstInGroup: boolean; isLastInGroup: boolean },
  ) => string;
  isSpecialColumn: (column: string) => boolean;
  isShadedColumn: (column: string) => boolean;
  handleToggleRow: (row: string) => void;
  handleCollapseAllRows: () => void;
  handleExpandAllRows: () => void;
  showXDim1Total: boolean;
  SHADED_COLUMN_BG: string;
  dataCellAlignClass?: string;
  noAggregateColumns?: string[]; // Columns that should NOT be aggregated (use original cell value)
}

export const BreakdownTableDataRow = React.memo(
  React.forwardRef<HTMLTableRowElement, BreakdownTableDataRowProps>(
    function BreakdownTableDataRow(
      {
        rowData: r,
        rowIndex,
        isLastRow,
        index,
        expandedRows,
        expandedColumns,
        visibleColumns,
        decoratedColumns,
        fillMissingSubRows,
        fillMissingSubColumns,
        subRowHasData,
        subColumnHasData,
        xDim2Order,
        yDim2Order,
        aggregateCellValues,
        renderCell,
        renderLabel,
        hideVerticalLines,
        cellNoVerticalBordersClasses,
        borderClasses,
        isSpecialColumn,
        isShadedColumn,
        handleToggleRow,
        handleCollapseAllRows,
        handleExpandAllRows,
        showXDim1Total,
        SHADED_COLUMN_BG,
        dataCellAlignClass,
        noAggregateColumns = [],
      }: BreakdownTableDataRowProps,
      ref,
    ) {
      const isLastDataRow = isLastRow && r.primary !== '__TOTAL__';

      // Stabilize toggle handlers with useCallback
      const handleToggleRowStable = useCallback(
        (e: React.MouseEvent) => {
          e.stopPropagation();
          if (e.shiftKey) {
            // Check if all rows with children are expanded
            const rowsWithChildren = index.rowValues.filter((row) =>
              hasSubDimensions(index, DIM_ROW, row),
            );
            const allExpanded = rowsWithChildren.every((row) =>
              expandedRows.has(row),
            );
            if (allExpanded) {
              handleCollapseAllRows();
            } else {
              handleExpandAllRows();
            }
          } else {
            handleToggleRow(r.primary);
          }
        },
        [
          handleToggleRow,
          handleCollapseAllRows,
          handleExpandAllRows,
          r.primary,
          index,
          expandedRows,
        ],
      );

      const handleDoubleClickRow = useCallback(
        (e: React.MouseEvent) => {
          e.stopPropagation();
          const rowsWithChildren = index.rowValues.filter((row) =>
            hasSubDimensions(index, DIM_ROW, row),
          );
          const allExpanded = rowsWithChildren.every((row) =>
            expandedRows.has(row),
          );
          if (allExpanded) {
            handleCollapseAllRows();
          } else {
            handleExpandAllRows();
          }
        },
        [index, expandedRows, handleCollapseAllRows, handleExpandAllRows],
      );

      const handleToggleForDimensionHeader = useCallback(() => {
        handleToggleRow(r.primary);
      }, [handleToggleRow, r.primary]);

      // Helper to render a cell
      const renderCellHelper = useCallback(
        (
          rParams: { primary: string; sub: string | null },
          cParams: { primary: string; sub: string | null },
        ): React.ReactNode => {
          // Look up the original cell from the data index
          const originalCell =
            rParams.primary !== '__TOTAL__' && cParams.primary !== '__TOTAL__'
              ? getCell(
                  index,
                  cParams.primary,
                  cParams.sub,
                  rParams.primary,
                  rParams.sub,
                )
              : undefined;

          // For columns in noAggregateColumns (e.g., percentage columns), use the original cell
          // value directly instead of aggregating. This prevents incorrect summing of percentages.
          const shouldSkipAggregation =
            noAggregateColumns.includes(cParams.primary) &&
            originalCell &&
            originalCell.value != null;

          let value: BreakdownCell['value'];
          if (shouldSkipAggregation) {
            value = originalCell.value;
          } else {
            const scope: Partial<AggregateForScopeParams> = {};
            if (rParams.primary !== '__TOTAL__') {
              scope.row = rParams.primary;
              scope.subRow = rParams.sub;
            }
            if (cParams.primary !== '__TOTAL__') {
              scope.column = cParams.primary;
              scope.subColumn = cParams.sub;
            }

            value = aggregateForScope(
              {
                index,
                ...scope,
                expandedRows,
                expandedColumns,
                fillMissingSubRows,
                fillMissingSubColumns,
                subRowHasData,
                subColumnHasData,
                xDim2Order,
                yDim2Order,
              } as AggregateForScopeParams,
              aggregateCellValues,
            );
          }

          const cell: BreakdownCell = {
            column: cParams.primary,
            subColumn: cParams.sub,
            row: rParams.primary,
            subRow: rParams.sub,
            value,
            metadata: originalCell?.metadata,
          };

          const context: BreakdownCellContext = {
            cell,
            column: cParams.primary,
            subColumn: cParams.sub,
            row: rParams.primary,
            subRow: rParams.sub,
            isGroupLevel: rParams.sub === null,
          };

          return renderCell(context);
        },
        [
          index,
          expandedRows,
          expandedColumns,
          fillMissingSubRows,
          fillMissingSubColumns,
          subRowHasData,
          subColumnHasData,
          xDim2Order,
          yDim2Order,
          aggregateCellValues,
          renderCell,
          noAggregateColumns,
        ],
      );

      const isSummaryRow = r.sub === null;

      return (
        <TableRow
          ref={ref}
          className={cn(
            'divide-x divide-border min-h-10',
            isSummaryRow && 'bg-muted',
          )}
          data-index={rowIndex}
        >
          {/* Y-axis label */}
          <TableCell
            className={cn(
              'sticky left-0 w-[280px] max-w-[280px] opacity-100 border-b border-border break-words',
              isSummaryRow ? 'bg-muted font-semibold' : 'bg-white pl-12',
              hideVerticalLines && 'border-r-0',
              r.hasChildren && isSummaryRow && 'cursor-pointer',
              cellNoVerticalBordersClasses,
            )}
            style={{
              zIndex: Z_INDEX.yAxis,
            }}
            onClick={
              r.hasChildren && r.sub === null
                ? handleToggleRowStable
                : undefined
            }
            onDoubleClick={
              r.hasChildren && r.sub === null ? handleDoubleClickRow : undefined
            }
          >
            <DimensionHeader
              label={
                r.sub ? index.subRowKeyToDisplay.get(r.sub) || r.sub : r.primary
              }
              expanded={r.expanded}
              hasChildren={r.hasChildren && r.sub === null}
              onToggle={
                r.hasChildren && r.sub === null
                  ? handleToggleForDimensionHeader
                  : undefined
              }
              renderLabel={renderLabel}
              isRow={r.sub === null}
              isSubRow={r.sub !== null}
            />
          </TableCell>

          {/* Data cells - matching header structure */}
          {index.columnValues.map((colValue) => {
            const hasChildren = hasSubDimensions(index, DIM_COL, colValue);
            const isExpanded = expandedColumns.has(colValue);

            if (hasChildren && isExpanded) {
              // Render cells for each visible sub-column
              const subCols = visibleColumns.filter(
                (vc) => vc.primary === colValue && vc.sub !== null,
              );

              return subCols.map((subCol, subIdx) => {
                const c = decoratedColumns.find(
                  (dc) => dc.primary === colValue && dc.sub === subCol.sub,
                );
                const cellBorders = c ? borderClasses(r, c) : '';

                return (
                  <TableCell
                    key={`${r.primary}-${r.sub}-${colValue}-${subCol.sub}-${subIdx}`}
                    className={cn(
                      cellBorders,
                      dataCellAlignClass,
                      isSummaryRow && 'bg-muted',
                      isShadedColumn(colValue) && SHADED_COLUMN_BG,
                      isSpecialColumn(colValue) &&
                        !isLastDataRow &&
                        'border-b border-border',
                      isSpecialColumn(colValue) &&
                        subIdx === 0 &&
                        !hideVerticalLines &&
                        'border-l-2 border-l-black',
                      isSpecialColumn(colValue) &&
                        subIdx === subCols.length - 1 &&
                        !hideVerticalLines &&
                        'border-r-2 border-r-black',
                      isSpecialColumn(colValue) &&
                        isLastDataRow &&
                        'border-b-2 border-b-black',
                    )}
                    style={{
                      zIndex: Z_INDEX.body,
                    }}
                  >
                    {renderCellHelper(r, {
                      primary: colValue,
                      sub: subCol.sub,
                    })}
                  </TableCell>
                );
              });
            } else {
              // Render single cell for collapsed or leaf columns
              const c = decoratedColumns.find(
                (dc) => dc.primary === colValue && dc.sub === null,
              );
              const cellBorders = c ? borderClasses(r, c) : '';

              return (
                <TableCell
                  key={`${r.primary}-${r.sub}-${colValue}`}
                  className={cn(
                    cellBorders,
                    dataCellAlignClass,
                    isSummaryRow && 'bg-muted',
                    isShadedColumn(colValue) && SHADED_COLUMN_BG,
                    isSpecialColumn(colValue) &&
                      !isLastDataRow &&
                      'border-b border-border',
                    isSpecialColumn(colValue) &&
                      !hideVerticalLines &&
                      'border-l-2 border-l-black border-r-2 border-r-black',
                    isSpecialColumn(colValue) &&
                      isLastDataRow &&
                      'border-b-2 border-b-black',
                  )}
                  style={{
                    zIndex: Z_INDEX.body,
                  }}
                >
                  {renderCellHelper(r, {
                    primary: colValue,
                    sub: null,
                  })}
                </TableCell>
              );
            }
          })}

          {/* Total column */}
          {showXDim1Total && (
            <TableCell
              className={cn(
                'bg-muted/40 font-semibold',
                dataCellAlignClass,
                !hideVerticalLines && 'border-l-2 border-r',
                cellNoVerticalBordersClasses,
              )}
            >
              {renderCellHelper(r, { primary: '__TOTAL__', sub: null })}
            </TableCell>
          )}
        </TableRow>
      );
    },
  ),
);
